import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  loadTermsOfService, 
  loadPrivacyPolicy, 
  loadRiskDisclaimer
} from '../utils/legalLoader';
import LandingNav from '../components/shared/LandingNav';

const legalDocs = [
  { id: 'terms', name: 'Terms of Service', loader: loadTermsOfService },
  { id: 'privacy', name: 'Privacy Policy', loader: loadPrivacyPolicy },
  { id: 'risk', name: 'Risk Disclosure', loader: loadRiskDisclaimer }
];

const Legal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDoc, setSelectedDoc] = useState('terms');
  const [legalContent, setLegalContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Parse URL hash to determine selected document
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && legalDocs.find(doc => doc.id === hash)) {
      setSelectedDoc(hash);
    } else {
      // Default to terms if no valid hash
      setSelectedDoc('terms');
    }
  }, [location.hash]);

  // Load the selected document
  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    
    const doc = legalDocs.find(d => d.id === selectedDoc);
    if (doc) {
      doc.loader().then(content => {
        setLegalContent(content);
        setLoading(false);
      });
    }
  }, [selectedDoc]);

  const handleDocSelect = (docId) => {
    setSelectedDoc(docId);
    navigate(`/legal#${docId}`);
  };

  return (
    <div style={{ padding: '24px 0', minHeight: '100vh' }}>
      {/* Navigation */}
      <LandingNav />

      {/* Main Content with Sidebar */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 20px', display: 'flex', gap: '32px' }}>
        {/* Left Sidebar */}
        <div style={{ 
          width: '280px', 
          flexShrink: 0,
          position: 'sticky',
          top: '100px',
          alignSelf: 'flex-start',
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '8px',
          padding: '20px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.2em' }}>Legal Documents</h3>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {legalDocs.map(doc => (
              <button
                key={doc.id}
                onClick={() => handleDocSelect(doc.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  color: selectedDoc === doc.id ? 'var(--accent)' : 'rgba(255, 255, 255, 0.7)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '1em',
                  transition: 'color 0.2s',
                  fontWeight: selectedDoc === doc.id ? '600' : '400'
                }}
                onMouseEnter={e => {
                  if (selectedDoc !== doc.id) {
                    e.target.style.color = '#fff';
                  }
                }}
                onMouseLeave={e => {
                  if (selectedDoc !== doc.id) {
                    e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                  }
                }}
              >
                {doc.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
          ) : legalContent ? (
            <>
              <h1 style={{ marginBottom: 8, marginTop: 0 }}>{legalContent.title}</h1>
              {legalContent.lastUpdated && (
                <div style={{ color: 'var(--text-light)', marginBottom: 24 }}>
                  Last Updated: {legalContent.lastUpdated}
                </div>
              )}

              {legalContent.intro && (
                <p style={{ marginBottom: 24, fontSize: '1.05em', lineHeight: '1.6' }}>{legalContent.intro}</p>
              )}

              {legalContent.sections.map((section, idx) => (
                <div key={idx} style={{ margin: '32px 0' }}>
                  {section.title && <h3 style={{ marginTop: 24, marginBottom: 12 }}>{section.title}</h3>}
                  <div style={{ opacity: 0.95 }}>
                    {section.content.map((item, itemIdx) => {
                      if (item.type === 'paragraph') {
                        return <p key={itemIdx} style={{ marginBottom: 16, lineHeight: '1.7' }}>{item.text}</p>;
                      } else if (item.type === 'bullet') {
                        return (
                          <ul key={itemIdx} style={{ marginLeft: 0, paddingLeft: 20, marginBottom: 12 }}>
                            <li style={{ marginBottom: 8, lineHeight: '1.6' }}>{item.text}</li>
                          </ul>
                        );
                      } else if (item.type === 'strong') {
                        const parts = item.text.split(':');
                        if (parts.length >= 2) {
                          return (
                            <p key={itemIdx} style={{ marginBottom: 16, lineHeight: '1.7' }}>
                              <strong>{parts[0]}:</strong> {parts.slice(1).join(':')}
                            </p>
                          );
                        }
                        return <p key={itemIdx} style={{ marginBottom: 16, lineHeight: '1.7' }}><strong>{item.text}</strong></p>;
                      }
                      return null;
                    })}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-light)' }}>
              Error loading document. Please try again.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Legal;

